/**
 * Launch context detection.
 *
 * The same web build powers both the public marketing site at allurexp.com
 * AND the native Android app (Capacitor WebView) installed from Google Play.
 *
 * We distinguish them so:
 *   - Regular browser visitors land on the public marketing site (/welcome).
 *   - Native app / PWA / installed-app users land directly in the existing intro/auth flow.
 *
 * Detection signals (any one is enough):
 *   1. `display-mode: standalone` (Chrome PWA / iOS PWA on iPad)
 *   2. `navigator.standalone` (iOS Safari home-screen install)
 *   3. document.referrer starts with 'android-app://' (legacy Android TWA from Play)
 *   4. URL has `?source=pwa` (start_url set in manifest.json + sticky in sessionStorage)
 *   5. User-agent contains 'AllureApp' (Capacitor WebView — appendUserAgent set in capacitor.config.json)
 *   6. localStorage flag set on a previous visit
 *
 * Once detected, we persist the flag in localStorage so the user stays in
 * "app mode" across full page reloads.
 */

const APP_MODE_FLAG = 'allureLaunchedAsApp';

let cachedMode = null;

function detectAppMode() {
  try {
    if (typeof window === 'undefined') return false;

    // 1. CSS display-mode standalone — strongest signal
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }

    // 2. iOS Safari home-screen install
    if (window.navigator && window.navigator.standalone === true) {
      return true;
    }

    // 3. Android TWA referrer (legacy — kept for backward compatibility)
    if (typeof document !== 'undefined' && document.referrer && document.referrer.startsWith('android-app://')) {
      return true;
    }

    // 4. URL query (set in manifest start_url and shortcuts)
    const params = new URLSearchParams(window.location.search);
    if (params.get('source') === 'pwa' || params.get('source') === 'pwa-shortcut') {
      return true;
    }

    // 5. Capacitor WebView — appendUserAgent in capacitor.config.json adds 'AllureApp' to UA
    if (typeof navigator !== 'undefined' && navigator.userAgent && navigator.userAgent.includes('AllureApp')) {
      return true;
    }

    // 6. Previously detected
    if (window.localStorage && window.localStorage.getItem(APP_MODE_FLAG) === '1') {
      return true;
    }
  } catch (_err) {
    // Fail closed → assume browser visitor.
  }
  return false;
}

export function isAppLaunch() {
  if (cachedMode === null) {
    cachedMode = detectAppMode();
    if (cachedMode) {
      try {
        window.localStorage.setItem(APP_MODE_FLAG, '1');
      } catch (_err) { /* ignore */ }
    }
  }
  return cachedMode;
}

/**
 * Force "app mode" when the user logs in via the marketing site — from that
 * point forward they're acting as an app user and logout should bring them back
 * to the marketing site only if they originally landed there. We DO NOT set
 * the persistent flag here; we just clear the cache so the next call re-checks.
 */
export function refreshLaunchContext() {
  cachedMode = null;
}

/**
 * Did the user originate from the public marketing website in this tab?
 * Used to decide where to send them on logout.
 */
const MARKETING_VISITOR_FLAG = 'allureCameFromMarketing';

export function markMarketingVisitor() {
  try {
    sessionStorage.setItem(MARKETING_VISITOR_FLAG, '1');
  } catch (_err) { /* ignore */ }
}

export function isMarketingVisitor() {
  try {
    return sessionStorage.getItem(MARKETING_VISITOR_FLAG) === '1';
  } catch (_err) {
    return false;
  }
}

export function clearMarketingVisitor() {
  try {
    sessionStorage.removeItem(MARKETING_VISITOR_FLAG);
  } catch (_err) { /* ignore */ }
}
